import React, { useState, useCallback, useRef } from 'react';
import {
  CloudArrowUpIcon,
  DocumentTextIcon,
  PhotoIcon,
  FilmIcon,
  XMarkIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

interface FileUpload {
  file: File;
  id: string;
  status: 'pending' | 'uploading' | 'success' | 'error';
  progress: number;
  error?: string;
}

interface DragDropUploaderProps {
  onFilesSelected: (files: File[]) => void;
  maxFiles?: number;
  maxFileSize?: number; // in MB
  acceptedTypes?: string[];
  disabled?: boolean;
  className?: string;
}

export default function DragDropUploader({
  onFilesSelected,
  maxFiles = 10,
  maxFileSize = 50,
  acceptedTypes = ['*'],
  disabled = false,
  className = ''
}: DragDropUploaderProps) {
  const [dragActive, setDragActive] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<FileUpload[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const generateId = () => Math.random().toString(36).substring(2, 15);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) return PhotoIcon;
    if (file.type.startsWith('video/')) return FilmIcon;
    return DocumentTextIcon;
  };

  const validateFile = (file: File): string | undefined => {
    // Size check
    if (file.size > maxFileSize * 1024 * 1024) {
      return `A fájl mérete meghaladja a ${maxFileSize}MB limitet`;
    }

    // Type check
    if (acceptedTypes.length > 0 && !acceptedTypes.includes('*')) {
      const fileExtension = file.name.split('.').pop()?.toLowerCase();
      const mimeType = file.type.toLowerCase();
      
      const isAccepted = acceptedTypes.some(type => {
        if (type.startsWith('.')) {
          return fileExtension === type.substring(1);
        }
        if (type.includes('/')) {
          return mimeType === type || mimeType.startsWith(type.replace('*', ''));
        }
        return fileExtension === type;
      });

      if (!isAccepted) {
        return `Nem támogatott fájltípus. Elfogadott típusok: ${acceptedTypes.join(', ')}`;
      }
    }

    return undefined;
  };

  const processFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const newFiles: FileUpload[] = [];
    const validFiles: File[] = [];

    // Check total file count
    if (selectedFiles.length + fileArray.length > maxFiles) {
      alert(`Maximum ${maxFiles} fájl tölthető fel egyszerre`);
      return;
    }

    fileArray.forEach(file => {
      const error = validateFile(file);
      const fileUpload: FileUpload = {
        file,
        id: generateId(),
        status: error ? 'error' : 'pending',
        progress: 0,
        error
      };

      newFiles.push(fileUpload);
      if (!error) {
        validFiles.push(file);
      }
    });

    setSelectedFiles(prev => [...prev, ...newFiles]);

    // Notify parent component of valid files
    if (validFiles.length > 0) {
      onFilesSelected(validFiles);
    }
  }, [selectedFiles, maxFiles, onFilesSelected]);

  const handleDrag = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDragIn = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setDragActive(true);
    }
  }, []);

  const handleDragOut = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFiles(e.dataTransfer.files);
    }
  }, [disabled, processFiles]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    
    if (e.target.files && e.target.files.length > 0) {
      processFiles(e.target.files);
      // Reset input
      e.target.value = '';
    }
  }, [disabled, processFiles]);

  const removeFile = (id: string) => {
    setSelectedFiles(prev => prev.filter(file => file.id !== id));
  };

  const openFileDialog = () => {
    if (disabled) return;
    fileInputRef.current?.click();
  };

  const getAcceptAttribute = () => {
    if (acceptedTypes.includes('*')) return '';
    return acceptedTypes.join(',');
  };

  return (
    <div className={`w-full ${className}`}>
      {/* Drop Zone */}
      <div
        className={`
          relative border-2 border-dashed rounded-xl p-8 text-center transition-all duration-200 cursor-pointer
          ${dragActive 
            ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20' 
            : 'border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500'
          }
          ${disabled ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'}
        `}
        onDragEnter={handleDragIn}
        onDragLeave={handleDragOut}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        onClick={openFileDialog}
      >
        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={getAcceptAttribute()}
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled}
        />

        <div className="space-y-4">
          <div className={`mx-auto w-16 h-16 rounded-full flex items-center justify-center transition-colors ${
            dragActive 
              ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' 
              : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500'
          }`}>
            <CloudArrowUpIcon className="w-8 h-8" />
          </div>

          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              {dragActive ? 'Engedje el a fájlokat itt' : 'Fájlok feltöltése'}
            </h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Húzza ide a fájlokat vagy kattintson a tallózáshoz
            </p>
            
            <div className="space-y-1 text-xs text-gray-400 dark:text-gray-500">
              <p>Maximum {maxFiles} fájl • Maximum {maxFileSize}MB fájlonként</p>
              {acceptedTypes.length > 0 && !acceptedTypes.includes('*') && (
                <p>Elfogadott típusok: {acceptedTypes.join(', ')}</p>
              )}
            </div>
          </div>

          {!dragActive && !disabled && (
            <button
              type="button"
              className="inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <CloudArrowUpIcon className="w-4 h-4 mr-2" />
              Fájlok kiválasztása
            </button>
          )}
        </div>
      </div>

      {/* Selected Files List */}
      {selectedFiles.length > 0 && (
        <div className="mt-6 space-y-3">
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            Kiválasztott fájlok ({selectedFiles.length})
          </h4>
          
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {selectedFiles.map((fileUpload) => {
              const FileIcon = getFileIcon(fileUpload.file);
              
              return (
                <div
                  key={fileUpload.id}
                  className="flex items-center p-3 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg"
                >
                  <div className="flex-shrink-0 mr-3">
                    <FileIcon className="w-6 h-6 text-gray-400" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                        {fileUpload.file.name}
                      </p>
                      <div className="flex items-center space-x-2 ml-2">
                        {fileUpload.status === 'success' && (
                          <CheckCircleIcon className="w-4 h-4 text-green-500" />
                        )}
                        {fileUpload.status === 'error' && (
                          <ExclamationTriangleIcon className="w-4 h-4 text-red-500" />
                        )}
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            removeFile(fileUpload.id);
                          }}
                          className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <XMarkIcon className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    
                    <div className="flex items-center justify-between mt-1">
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {formatFileSize(fileUpload.file.size)}
                      </p>
                      
                      {fileUpload.error && (
                        <p className="text-xs text-red-500 truncate ml-2">
                          {fileUpload.error}
                        </p>
                      )}
                    </div>

                    {/* Progress Bar */}
                    {fileUpload.status === 'uploading' && (
                      <div className="mt-2">
                        <div className="bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                          <div
                            className="bg-blue-600 h-1.5 rounded-full transition-all duration-300"
                            style={{ width: `${fileUpload.progress}%` }}
                          />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                          {fileUpload.progress}% feltöltve
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Clear All Button */}
          <button
            onClick={() => setSelectedFiles([])}
            className="text-sm text-gray-500 dark:text-gray-400 hover:text-red-500 dark:hover:text-red-400 transition-colors"
          >
            Összes törlése
          </button>
        </div>
      )}
    </div>
  );
}
